import React, {useEffect} from 'react';

import 'core-js/features/array/at';
import 'globalthis-polyfill';
import type {AppProps} from 'next/app';
import NextHead from 'next/head';
import NextScript from 'next/script';
import {polyfill as smoothScrollPolyfill} from 'smoothscroll-polyfill';

import {HeadProject} from '@specials/frontend-head';
import {HeadScriptStartupErrorHandler} from '@specials/frontend-startup-error-handler';
import {
  ResourcesLoader,
  ResourcesLoaderProps,
  createResourcesLoaderPropsLocal,
  createResourcesLoaderPropsRemote,
} from '@specials/resources';
import {HeadScriptSentryBrowser} from '@specials/sentry-browser';
import {HeadScriptTracking, ScriptTracking} from '@specials/web-tracking';

import {Environment, envs} from '@/envs';
import {ResourcesAll} from '@/resources';
import head from '@/resources/head';
import {AppDIContainer} from '@/ui/common/AppDiContainer/AppDiContainer';
import {AppSecretCodeChecker} from '@/ui/common/AppSecretCodeChecker/AppSecretCodeChecker';
import {ErrorBoundary} from '@/ui/common/ErrorBoundary/ErrorBoundary';
import {SafeHydrate} from '@/ui/common/SafeHydrate/SafeHydrate';

import '@/ui/styles/global.css';

export default function MyApp({Component, pageProps}: AppProps): JSX.Element {
  useEffect(() => {
    // полифилл для плавного скролла
    // без него в некоторых мобилках не работает scrollTo с опциями
    smoothScrollPolyfill();
  }, []);

  // @See: https://jira.tcsbank.ru/browse/SP-19881
  const allowUrls = [envs.APP_STATIC_URL, envs.APP_PROJECT_URL];

  return (
    <>
      <HeadProject wrapper={NextHead} {...head} />
      <HeadScriptTracking head={NextHead} productName={envs.APP_ANALYTICS_PRODUCT_NAME} />

      <HeadScriptSentryBrowser
        wrapper={NextHead}
        appStaticUrl={envs.APP_STATIC_URL}
        sentryBrowserParams={{
          // нужно отклчючить логирование ошибок как евентов, чтоб они попали в дашборд ErrorHub
          captureExceptionAsEvent: false,
          formattersOptions: {
            // добавляем response запросов в breadcrumbs,
            // чтобы видеть какой ответ пришел от бэкенда в запросе, предшествующем ошибке
            breadcrumbsResponse: true,
            // очищаем extras от полей со значением undefined, потому что тем полям,
            // которые не указаны для отображения в поле extras,
            // sentry sdk автоматически ставит значение undefined
            extras: true,
            // обрабатываем аргументы breadcrumbs,
            // потому что поле breadcrumbs.data.arguments может быть строкой,
            // из-за чего событие улеатает в dead-letters
            breadcrumbs: true,
            // исключаем вероятность пересчения названия поля payload для разных проектов,
            // чтоб события не улетали в dead-letters из-за несовпадений типов данных
            payloadPostfix: envs.APP_SENTRY_PAYLOAD_POSTFIX,
          },
          sentryOptions: {
            dsn: envs.APP_SENTRY_DSN,
            environment: envs.APP_ENVIRONMENT,
            enabled: envs.APP_ENVIRONMENT !== Environment.Local,
            release: process.env.ERRORHUB_RELEASE,
            allowUrls,
          },
        }}
      />
      <HeadScriptStartupErrorHandler wrapper={NextHead} appStaticUrl={envs.APP_STATIC_URL} />

      <ResourcesLoader {...buildResourcesLoaderParams()}>
        <SafeHydrate>
          <AppDIContainer>
            <AppSecretCodeChecker>
              <ErrorBoundary>
                <Component {...pageProps} />
              </ErrorBoundary>
            </AppSecretCodeChecker>
          </AppDIContainer>
        </SafeHydrate>
      </ResourcesLoader>
      <ScriptTracking script={NextScript} />
    </>
  );
}

function buildResourcesLoaderParams(): ResourcesLoaderProps<ResourcesAll> {
  // Используем process.env и require для локального файла ресурсов,
  // потому что иначе tree shaking не сможет вырезать файл локальных ресурсов
  // при использовании ресурсов из отдельного репозитория.
  // Tree shaking работает только если в if использовать прямое обращение к process.env
  if (process.env.APP_RESOURCES_USE_LOCAL === 'true') {
    return createResourcesLoaderPropsLocal(require('@/resources').resources);
  } else {
    return createResourcesLoaderPropsRemote(envs.APP_RESOURCES_URL);
  }
}
